import { useEffect, useState, useCallback, useMemo } from "react";
import { usePostHog } from "posthog-js/react";
import { View, StyleSheet, Image } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { MotiView } from "moti";
import { useGetCourseModulesLiteQuery, useGetCourseProgressInformationQuery } from "../graphql/generated/graphql";
import CustomProgressBar from "../components/common/general/CustomProgressBar/CustomProgressBar";
import JourneyBreadcrumbs from "../components/journey/JourneyBreadcrumbs/JourneyBreadcrumbs";
import Skeleton from "react-loading-skeleton";
import AiTutorCardProgressListContainer from "../containers/school/AiTutorCardProgressListContainer/AiTutorCardProgressListContainer";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import type { RootStackParamList } from "../navigation/navigation.types";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { useActiveCourse, ResourceType } from "../contexts/ActiveCourseContext";
import { useAuth } from "../hooks/useAuth";
import CustomButton from "../components/common/general/CustomButton/CustomButton";

type CustomModuleStudentViewRouteProp = RouteProp<RootStackParamList, "Custom Module Student">;
type CustomModuleStudentViewNavigationProp = NativeStackNavigationProp<RootStackParamList, "Custom Module Student">;

const CustomModuleStudentView = () => {
  const navigation = useNavigation<CustomModuleStudentViewNavigationProp>();
  const route = useRoute<CustomModuleStudentViewRouteProp>();
  const posthog = usePostHog();
  const { isLarge } = useResponsiveScreen();
  const { setActiveResource } = useActiveCourse();

  const { module_id: moduleId, course_id: courseId, integration_id: integrationId } = route.params;

  const { userInfo } = useAuth();

  const {
    data: courseData,
    loading,
    error,
  } = useGetCourseModulesLiteQuery({
    variables: { id: courseId },
    fetchPolicy: "cache-and-network",
  });

  const { data: courseProgressInformationData } = useGetCourseProgressInformationQuery({
    variables: { courseId },
    skip: !courseId,
  });

  const course = courseData?.getCourseModulesLite?.data?.[0];
  const currentModule = useMemo(() => course?.modules?.find((m) => m.id === moduleId), [course?.modules, moduleId]);

  const moduleProgressInformation = courseProgressInformationData?.getCourseProgressInformation?.data?.modules?.find(
    (m) => m.id === moduleId
  );

  const currentModuleIndex = useMemo(
    () => (currentModule != null ? (course?.moduleOrder?.indexOf(currentModule.id) ?? -1) : -1),
    [currentModule, course?.modules, course?.moduleOrder]
  );

  const nextModuleId = useMemo(
    () => (currentModuleIndex !== -1 ? course?.moduleOrder?.[currentModuleIndex + 1] : undefined),
    [currentModuleIndex, course?.moduleOrder]
  );

  useFocusEffect(
    useCallback(() => {
      if (currentModule) {
        setActiveResource({ resourceType: ResourceType.MODULE, resourceId: currentModule.id });
      }
      return () => {
        const currentRoute = navigation.getState()?.routes[navigation.getState()?.index];
        const isChildResourceView =
          currentRoute?.name === "Custom Assignment Student" ||
          currentRoute?.name === "Custom Resource Student" ||
          currentRoute?.name === "Custom Course AITutor" ||
          currentRoute?.name === "Project";

        if (!isChildResourceView) {
          setActiveResource(null);
        }
      };
    }, [currentModule, setActiveResource, navigation])
  );

  useEffect(() => {
    if (currentModule && userInfo.userId) {
      posthog.identify(userInfo.userId);
      posthog.capture("module_view", {
        module_id: currentModule.id,
        module_title: currentModule.title,
        course_id: courseId,
        course_name: course?.name,
        user_id: userInfo.userId,
      });
    }
  }, [currentModule, courseId, course?.name, userInfo.userId, posthog]);

  const navigateToNextModule = useCallback(() => {
    if (nextModuleId != null) {
      navigation.navigate("Custom Module Student", {
        course_id: courseId,
        module_id: nextModuleId,
        integration_id: integrationId,
      });
    }
  }, [nextModuleId, navigation, courseId, integrationId]);

  const renderModuleHeader = () => {
    if (currentModule?.thumbnail) {
      return (
        <View style={{ flexDirection: isLarge ? "row" : "column", gap: isLarge ? 100 : 16 }}>
          {/* Text column */}
          <View style={[styles.headerTextCol, { width: isLarge ? "50%" : "100%" }]}>
            <View>
              <CustomText text="MODULE" size="m" weight="medium" style={{ marginBottom: 6 }} />
              <CustomText
                text={currentModule.title || "Untitled Module"}
                size="s"
                textType="display"
                weight="ultraBold"
                useTranslationText={false}
                role="heading"
                aria-level="1"
              />
              {!!currentModule.description && (
                <CustomText
                  text={currentModule.description}
                  size="l"
                  weight="normal"
                  style={styles.moduleDescription}
                  useTranslationText={false}
                  role="heading"
                  aria-level="2"
                />
              )}
            </View>

            <View style={styles.progressBox}>
              <View style={styles.progressHeader}>
                <CustomText
                  text="Module Progress"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
                <CustomText
                  text={`${Math.round(moduleProgressInformation?.progress || 0)}% Complete`}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              <CustomProgressBar
                progress={moduleProgressInformation?.progress || 0}
                color={moduleProgressInformation?.progress >= 100 ? "#22C55E" : "#3E68FE"}
                unfilledColor="#EAECF0"
                ariaLabel={`Module progress: ${Math.round(moduleProgressInformation?.progress || 0)}% complete`}
              />
            </View>
          </View>

          {/* Image column */}
          <View style={{ width: isLarge ? "50%" : "100%" }}>
            <Image
              source={{ uri: currentModule.thumbnail }}
              accessibilityRole="image"
              accessibilityLabel={`${currentModule.title} thumbnail`}
              alt={`${currentModule.title} thumbnail`}
              resizeMode="cover"
              style={styles.headerImage}
            />
          </View>
        </View>
      );
    }

    // No thumbnail
    return (
      <View style={styles.headerNoImage}>
        <View style={{ marginBottom: 0 }}>
          <CustomText
            text={currentModule?.title || "Untitled Module"}
            size="s"
            textType="display"
            weight="ultraBold"
            style={styles.moduleTitle}
            useTranslationText={false}
          />
          {!!currentModule?.description && (
            <CustomText
              text={currentModule.description}
              size="l"
              weight="normal"
              style={styles.moduleDescription}
              useTranslationText={false}
            />
          )}
        </View>

        <CustomSpacing type="vertical" size="l" />

        <View style={styles.progressBox}>
          <View style={styles.progressHeader}>
            <CustomText text="Module Progress" size="s" weight="bold" useTranslationText={false} />
            <CustomText
              text={`${Math.round(moduleProgressInformation?.progress || 0)}% Complete`}
              size="s"
              weight="bold"
              useTranslationText={false}
            />
          </View>
          <CustomSpacing type="vertical" size="s" />
          <CustomProgressBar
            progress={moduleProgressInformation?.progress || 0}
            color={moduleProgressInformation?.progress >= 100 ? "#22C55E" : "#3E68FE"}
            unfilledColor="#EAECF0"
            ariaLabel={`Module progress: ${Math.round(moduleProgressInformation?.progress || 0)}% complete`}
          />
        </View>
      </View>
    );
  };

  if (loading) {
    return (
      <DefaultLayout pageHasChat={false}>
        <View style={styles.container}>
          <Skeleton height={220} style={{ marginBottom: 20 }} />
          <Skeleton height={40} width="60%" style={{ marginBottom: 10 }} />
          <Skeleton height={20} width="80%" style={{ marginBottom: 20 }} />
          {[1, 2, 3].map((idx) => (
            <Skeleton key={idx} height={80} style={{ marginBottom: 10 }} />
          ))}
        </View>
      </DefaultLayout>
    );
  }

  if (!loading && (error || !course || !currentModule)) {
    return (
      <DefaultLayout>
        <View style={styles.container}>
          <CustomText text="Module not found or error loading data" size="m" weight="medium" />
        </View>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.container}>
        <JourneyBreadcrumbs
          onPress={() =>
            navigation.navigate("Custom Course Student", {
              courseId: courseId,
              integration_id: integrationId,
            })
          }
          firstItem={course?.name || "Course"}
          secondItem={currentModule?.title || "Module"}
        />

        {renderModuleHeader()}

        <CustomSpacing type="vertical" size="xl" />

        <MotiView
          from={{ opacity: 0, translateY: 20 }}
          animate={{ opacity: 1, translateY: 0 }}
          delay={300}
          transition={{
            opacity: { type: "timing", duration: 600 },
            translateY: { type: "timing", duration: 600 },
          }}
          style={styles.contentContainer}>
          <AiTutorCardProgressListContainer integrationId={integrationId} courseId={courseId} moduleId={moduleId} />
          {nextModuleId != null && (
            <View style={styles.bottomActionRow}>
              <CustomButton styleType="light" text="Next" onPress={navigateToNextModule} />
            </View>
          )}
        </MotiView>
      </View>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
    width: "100%",
    maxWidth: 1200,
    alignSelf: "center",
  },
  headerTextCol: {
    flex: 1,
    justifyContent: "space-between",
    paddingVertical: 16,
  },

  headerImage: {
    width: "100%",
    height: 260,
    borderRadius: 12,
  },
  moduleTitle: {
    color: "#1F2937",
    lineHeight: 40,
  },
  moduleDescription: {
    color: "#101828",
    lineHeight: 28,
    marginTop: 8,
  },
  progressBox: {
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    padding: 20,
    marginTop: 16,
  },
  progressHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerNoImage: { paddingVertical: 0 },

  contentContainer: { flex: 1 },
  bottomActionRow: {
    marginTop: 24,
    width: "100%",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
});

export default CustomModuleStudentView;
